import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Icon,
  InputSelect,
  InputSwitch,
  Link,
  Stack,
  Text,
  Title,
} from "@bookingcom/bui-react";
import CloseIcon from "@bookingcom/bui-assets-react/streamline/CloseIcon";
import { localStorageConfigItems, setLocalStorageConfig } from "../../stores/localStorageConfigStore";
import { setLocalStorageByKey } from "../../stores/localStorageStore";
import type { Environment } from "../../types";
import { useDevToolsPanelController } from "./DevToolsPanel.controller";
import styles from "./DevToolsPanel.module.css";

type DevToolsPanelContentProps = {
  environment: Environment;
  setIsOpen: (value: boolean) => void;
};

export const DevToolsPanelContent = ({ environment, setIsOpen }: DevToolsPanelContentProps) => {
  const {
    envBadgeVariant,
    appVersion,
    appVersionUrl,
    language,
    localStorageConfigState,
    setLocalStorageConfigState,
    shouldReload,
  } = useDevToolsPanelController({
    environment,
  });

  return (
    <Card className={styles.panel} fill={true}>
      <Box mixin={{ paddingBlockStart: 2, paddingBlockEnd: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Text variant="strong_1">Dev Tools</Text>
          <Button
            icon={CloseIcon}
            variant="tertiary-neutral"
            size="medium"
            onClick={() => {
              setLocalStorageConfig(localStorageConfigState);
              setIsOpen(false);
              if (shouldReload()) {
                window.location.reload();
              }
            }}
          />
        </Stack>
      </Box>
      <Divider />

      <Box>
        <Stack>
          {appVersion && appVersionUrl && (
            <Stack direction="row" justifyContent="space-between">
              <Title title="App version" />
              <Link href={appVersionUrl} attributes={{ target: "_blank" }}>
                {appVersion}
              </Link>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between">
            <Title title="Environment" />
            <Badge
              variant={envBadgeVariant}
              text={environment.env}
              attributes={{ style: { paddingBlock: 0, paddingInline: 2 } }}
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Title title="Locale" />
            <Badge
              variant="constructive"
              alternative={true}
              text={language}
              attributes={{ style: { paddingBlock: 0, paddingInline: 2 } }}
            />
          </Stack>
        </Stack>
      </Box>
      <Divider />

      <Box>
        <Stack gap={2}>
          {localStorageConfigItems.map((item) => {
            // override user role only applicable when MSW mock is enabled
            if (!localStorageConfigState.psmConfigEnableMSW && item.localStorageKey === "psmConfigOverrideUserRole") {
              return null;
            }

            return (
              <>
                <Stack
                  key={`kLocalStorageConfigItem-${item.localStorageKey}`}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" gap={4}>
                    <Icon svg={item.icon} size="medium" />
                    <Title title={item.title} subtitle={item.description} />
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={4}>
                    {item.type === "string" &&
                      item.possibleValues.length &&
                      localStorageConfigState[item.localStorageKey] && (
                        <InputSelect
                          name={item.localStorageKey}
                          size="medium"
                          options={item.possibleValues.map((possibleValue) => ({
                            text: possibleValue,
                            value: possibleValue,
                          }))}
                          defaultValue={localStorageConfigState[item.localStorageKey]}
                          onChange={({ value }) => setLocalStorageConfigState({ [item.localStorageKey]: value })}
                        />
                      )}
                    <InputSwitch
                      name={item.localStorageKey}
                      value={!!localStorageConfigState[item.localStorageKey]}
                      onChange={({ value }) => {
                        if (item.type === "boolean") {
                          setLocalStorageConfigState({ [item.localStorageKey]: value });
                          // reset the onboarding tour step from localStorage
                          if (item.localStorageKey === "psmConfigFirstLogin" && value) {
                            setLocalStorageByKey("psmOnboardingTourStep", undefined);
                          }
                        } else if (item.type === "string") {
                          setLocalStorageConfigState({
                            [item.localStorageKey]:
                              value && item.possibleValues.length ? item.possibleValues[0] : undefined,
                          });
                        }
                      }}
                    />
                  </Stack>
                </Stack>
                <Divider />
              </>
            );
          })}
        </Stack>
      </Box>
    </Card>
  );
};
